import React, { Component } from 'react'
import { Calendar, momentLocalizer, Navigate } from 'react-big-calendar'
import { Button, DatePicker, Space, Row, Col } from 'antd'

import moment from 'moment'
import uniq from 'lodash/uniq'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Prompt, withRouter } from 'react-router-dom'
import { withApollo } from '@apollo/client/react/hoc'
import { RRule, rrulestr } from 'rrule'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { v4 as uuidv4 } from 'uuid'
import { faEdit, faSave } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

import EditScheduleEvent from './EditScheduleEvent'
import { Colors } from '../../components/Styles'
import * as Utils from '../../components/Utils'
import { openCustomConfirmModal } from '../../components/CustomConfirmModal'
import ScheduleTimezoneSelect, {
	LOCAL_TIMEZONE,
	deviceTimezoneOptions,
	initialTimezoneKey,
	nowGetter,
	persistTimezoneKey
} from '../../components/ScheduleTimezoneSelect'
import { MessageProgramScheduleDevicesQuery } from './Queries'

const localizer = momentLocalizer(moment)

const formats = {
	dayFormat: (date, culture, localizer) =>
		localizer.format(date, 'ddd D', culture),

	timeGutterFormat: (time, culture, localizer) =>
		localizer.format(time, 'HH:mm', culture),

	eventTimeRangeFormat: ({ start }, culture, localizer) =>
		localizer.format(start, 'HH:mm', culture)
}

const DnDCalendar = withDragAndDrop(Calendar)

// the week containing the given date, as displayed by the calendar
const weekRange = (date) => ({
	start: moment(date).weekday(0).hour(0).minute(0).second(0).toDate(),
	end: moment(date)
		.weekday(6)
		.add(1, 'days')
		.hour(0)
		.minute(0)
		.second(0)
		.toDate()
})

const CustomCalendarToolbar = ({
	date,
	onNavigate,
	timezoneSelector,
	actions
}) => {
	return (
		// the datepicker keeps its width and the actions take the rest,
		// the gutter separates them when they wrap on small screens
		<Row style={{ marginTop: 10, marginBottom: 10 }} gutter={[0, 8]}>
			<Col flex="none">
				<Button.Group style={{ margin: 0 }}>
					<Button onClick={() => onNavigate(Navigate.PREVIOUS)}>
						&lt;&lt;
					</Button>
				</Button.Group>

				<DatePicker
					showTime={false}
					picker="week"
					format="DD-MM-YYYY"
					value={moment(date)}
					onChange={(v) => {
						if (v) {
							onNavigate(Navigate.DATE, v.toDate())
						}
					}}
					renderExtraFooter={() => (
						<div
							style={{ textAlign: 'center', cursor: 'pointer' }}
							onClick={() => onNavigate(Navigate.TODAY)}
						>
							<a>This week</a>
						</div>
					)}
				/>

				<Button.Group style={{ margin: 0 }}>
					<Button onClick={() => onNavigate(Navigate.NEXT)}>
						&gt;&gt;
					</Button>
				</Button.Group>
			</Col>

			<Col
				flex="auto"
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center'
				}}
			>
				<Space wrap>
					{timezoneSelector}
					{actions}
				</Space>
			</Col>
		</Row>
	)
}

class Scheduler extends Component {
	state = {
		calendarEvents: null,
		events: {},
		editing: null,
		height: null,
		start: null,
		end: null,
		editable: false,
		dirty: false,
		// id of an event created by a slot selection but not yet confirmed
		// in the edit dialog, so it can be discarded on cancel
		creating: null,
		saving: false,
		// the calendar date, controlled to open on the week of the selected timezone
		date: new Date(),
		// devices (and the local time) whose timezone can be used for the current time
		timezoneOptions: [],
		timezoneKey: LOCAL_TIMEZONE,
		getNow: () => new Date()
	}

	componentDidMount() {
		this.createEvents(this.props)
		this.loadTimezoneOptions(this.props)
		window.addEventListener('beforeunload', this.onBeforeUnload)
	}

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.onBeforeUnload)
	}

	onBeforeUnload = (e) => {
		if (this.state.dirty) {
			e.preventDefault()
			e.returnValue = ''
		}
	}

	componentWillReceiveProps(props) {
		if (
			props.messageProgram &&
			this.props.messageProgram._id !== props.messageProgram._id
		) {
			this.setState({ dirty: false, editable: false, creating: null })
			this.createEvents(props)
			this.loadTimezoneOptions(props)
		} else if (
			((props.messageProgram &&
				this.props.messageProgram.updatedBy?.date !==
					props.messageProgram.updatedBy?.date) ||
				props.messagePrograms) &&
			!this.state.dirty
		) {
			this.createEvents(props)
		}
	}

	programEvents = (messageProgram, color) => {
		let events = {}
		if (messageProgram.schedule) {
			const schedule = JSON.parse(messageProgram.schedule)
			let medias = {}
			for (let f of messageProgram.medias.filter(
				(media) => media !== null
			)) {
				medias[f._id] = f
			}

			for (let evt of schedule) {
				if (evt.rule) {
					const id = uuidv4()
					events[id] = {
						...evt,
						id,
						media: medias[evt.media],
						color
					}
				}
			}
		}
		return events
	}

	createEvents = (props) => {
		let events = {}
		if (props.messageProgram) {
			events = this.programEvents(props.messageProgram)
		} else if (props.messagePrograms) {
			for (let i in props.messagePrograms) {
				const prog = props.messagePrograms[i]
				events = {
					...events,
					...this.programEvents(
						prog,
						Colors.playlistColors[i % Colors.playlistColors.length]
					)
				}
			}
		}

		const { start, end } =
			this.state.start && this.state.end ?
				this.state
			:	weekRange(this.getNow())

		let calendarEvents = this.createCalendarEvents(events, start, end)

		// console.log(events, calendarEvents)
		this.setState({ events, calendarEvents, start, end })
	}

	// the message program devices with a known timezone, or the device
	// whose zone message programs are displayed (device page)
	loadTimezoneOptions = async ({ messageProgram, device }) => {
		let options = []
		if (device) {
			options = deviceTimezoneOptions([device])
		} else if (messageProgram) {
			try {
				const res = await this.props.client.query({
					query: MessageProgramScheduleDevicesQuery,
					variables: { id: messageProgram._id }
				})
				options = deviceTimezoneOptions(
					(res.data?.messageProgramById?.zones || []).map(
						(z) => z.device
					)
				)
			} catch (e) {
				// fall back to the local time
				console.error(e)
			}

			// the message program changed while loading
			if (this.props.messageProgram?._id !== messageProgram._id) {
				return
			}
		}

		const timezoneKey = initialTimezoneKey(
			options,
			this.props.location.search
		)
		this.setState(
			{
				timezoneOptions: options,
				timezoneKey,
				getNow: nowGetter(options, timezoneKey)
			},
			() => {
				// open the calendar on the current week of the selected timezone
				const date = this.getNow()
				const { start, end } = weekRange(date)
				this.setState({
					date,
					start,
					end,
					calendarEvents: this.createCalendarEvents(
						this.state.events,
						start,
						end
					)
				})
			}
		)
	}

	selectTimezone = (timezoneKey) => {
		this.setState((state) => ({
			timezoneKey,
			getNow: nowGetter(state.timezoneOptions, timezoneKey)
		}))
		// the device page has no device to remember in its url
		if (!this.props.device) {
			persistTimezoneKey(timezoneKey)
		}
	}

	getNow = () => this.state.getNow()

	setRange = (dates) => {
		if (dates.length > 0) {
			const start = dates[0]
			const end = moment(dates[dates.length - 1])
				.add(1, 'days')
				.toDate()
			const calendarEvents = this.createCalendarEvents(
				this.state.events,
				start,
				end
			)

			this.setState({
				start,
				end,
				calendarEvents
			})
		}
	}

	createCalendarEvents = (
		events,
		from = this.state.start,
		until = this.state.end
	) => {
		let calendarEvents = []
		for (let id in events) {
			const evt = events[id]
			const eventRule = rrulestr(evt.rule)
			const localFrom = Utils.localToUtcDate(from)
			const localUntil = Utils.localToUtcDate(until)

			const eventTimes = eventRule.between(
				localFrom.toDate(),
				localUntil.toDate()
			)

			// The times we're getting from rrule are UTC times but the browser converts them to local times
			// What we want are local times with the same hour as the UTC time
			// So we subtract the local tz offset from the UTC time
			const eventDayEvents = eventTimes.map((e) => {
				let localTime = Utils.utcToLocalDate(e)
				return {
					id: evt.id,
					start: localTime.toDate(),
					end: localTime.add(30, 'minutes').toDate()
				}
			})

			calendarEvents = [...calendarEvents, ...eventDayEvents]
		}
		return calendarEvents
	}

	onEventDrop = (drop) => {
		if (this.state.editable && drop.event.id) {
			const event = this.state.events[drop.event.id]

			if (event) {
				// update rule with start date
				let oldRule = rrulestr(event.rule)
				let localTime = Utils.localToUtcDate(drop.start)
				let oldRuleSet

				// for rule sets we need to get the internal rule
				if (oldRule._exdate) {
					oldRuleSet = oldRule
					oldRule = oldRule._rrule[0]
				}

				const eventStartDate = Utils.localToUtcDate(drop.event.start)
				const oldStartDate = moment(oldRule.options.dtstart)

				const editRule = () => {
					let newRule = new RRule({
						dtstart: localTime.toDate(),
						freq: oldRule.options.freq,
						until: oldRule.options.until,
						interval: oldRule.options.interval,
						count: oldRule.options.count,
						byweekday: oldRule.options.byweekday,
						bymonthday: oldRule.options.bymonthday
					})

					// If old rule was a set recreate it
					if (oldRuleSet) {
						newRule = Utils.updatedRuleSetWithRule(
							oldRuleSet,
							newRule
						)
					}

					this.setEvent(drop.event.id, newRule.toString())
				}

				// For reccuring events, ask change occurence only or all except for the first event
				if (
					oldRule.options.count !== 1 &&
					!eventStartDate.isSame(oldStartDate)
				) {
					const { intl } = this.props
					openCustomConfirmModal({
						title: intl.formatMessage({
							id: 'messagePrograms.details.edit.confirm.title'
						}),
						content: intl.formatMessage(
							{
								id: 'messagePrograms.details.edit.confirm.message'
							},
							{
								date: intl.formatDate(drop.event.start, {
									day: 'numeric',
									month: 'long',
									year: 'numeric',
									hour: '2-digit',
									minute: '2-digit'
								})
							}
						),
						primaryActionText: intl.formatMessage({
							id: 'messagePrograms.details.confirm.all'
						}),
						secondaryActionText: intl.formatMessage({
							id: 'messagePrograms.details.confirm.instanceOnly'
						}),
						cancelText: intl.formatMessage({
							id: 'actions.cancel'
						}),
						onPrimaryAction: () => {
							// Edit all occurences
							// Keep start date on the same day but change time
							localTime = Utils.localToUtcDate(
								drop.start,
								oldStartDate.toDate()
							) // The offset needs to be calculated based on the old date
							localTime.date(oldStartDate.date())
							localTime.month(oldStartDate.month())
							localTime.year(oldStartDate.year())

							editRule()
						},
						onSecondaryAction: () => {
							// Edit single occurence
							// Delete specific event and create a new identical one at the dropped time
							this.setState((state) => {
								let events = state.events

								// Exclude drop date from event
								let rruleSet = rrulestr(event.rule, {
									forceset: true
								})
								rruleSet.exdate(
									Utils.localToUtcDate(
										drop.event.start
									).toDate()
								)
								events[event.id].rule = rruleSet.toString()

								// Create new single event at drop date
								let newEvent = {
									id: uuidv4(),
									rule: new RRule({
										dtstart: Utils.localToUtcDate(
											drop.start
										).toDate(),
										count: 1
									}).toString(),
									media: event.media
								}
								events[newEvent.id] = newEvent

								const calendarEvents =
									this.createCalendarEvents(events)
								return { calendarEvents, events, dirty: true }
							})
						}
					})
				} else {
					editRule()
				}
			}
		}
	}

	setEvent = (id, rule = null, media = null) => {
		if (this.state.editable) {
			// Buffer the modifications in memory, they are only sent
			// to the backend when the user saves
			this.setState((state) => {
				let events = state.events
				if (rule || media) {
					let event = state.events[id]

					if (event) {
						if (rule) {
							event.rule = rule
						}
						if (media) {
							event.media = media
						}

						events[id] = event
					}
				} else {
					delete events[id]
				}

				const calendarEvents = this.createCalendarEvents(events)

				return { calendarEvents, events, dirty: true }
			})
		}
	}

	save = async () => {
		this.setState({ saving: true })
		try {
			let mediaIds = [...this.props.messageProgram.mediaIds]
			let savedEvents = []
			for (let evtId in this.state.events) {
				const event = this.state.events[evtId]
				if (event.media) {
					mediaIds.push(event.media._id)
					savedEvents.push({
						rule: event.rule,
						media: event.media._id
					})
				}
			}

			await this.props.save(savedEvents, uniq(mediaIds))
			this.setState({ dirty: false, editable: false, creating: null })
		} catch (e) {
			Utils.displayError(e)
		} finally {
			this.setState({ saving: false })
		}
	}

	cancel = () => {
		this.setState(
			{
				dirty: false,
				editable: false,
				editing: null,
				creating: null
			},
			() => this.createEvents(this.props)
		)
	}

	handleSelect = (calEvent) => {
		if (this.state.editable) {
			this.setState((state) => {
				let event
				let creating = null
				let events = { ...state.events }
				if (calEvent.id) {
					event = this.state.events[calEvent.id]
				} else {
					// The dialog subtracts the offset so that the UTC date from events show in local time
					// But calEvent.start is already in local time, so we compensate here
					let localTime = Utils.localToUtcDate(calEvent.start)
					event = {
						id: uuidv4(),
						rule: new RRule({
							dtstart: localTime.toDate(),
							count: 1
						}).toString()
					}
					// the event goes in the buffer right away so the dialog can
					// edit it, mark it as pending confirmation
					events[event.id] = event
					creating = event.id
				}
				event.start = calEvent.start

				return {
					editing: event,
					events,
					creating
				}
			})
		}
	}

	titleAccessor = (calEvent) => {
		const event = this.state.events[calEvent.id]
		if (event) {
			return event.media ?
					event.media.name
				:	this.props.intl.formatMessage({
						id: 'messagePrograms.details.noFileSelected'
					})
		}
		return ''
	}

	renderActions = () => {
		if (!this.props.editable) {
			return null
		}

		if (!this.state.editable) {
			return (
				<Button
					type="primary"
					dataTest="message-schedule-edit-button"
					onClick={() => this.setState({ editable: true })}
				>
					<FontAwesomeIcon icon={faEdit} />
					&nbsp;
					<FormattedMessage id="actions.edit" />
				</Button>
			)
		}

		return (
			<Space>
				<Button
					type="primary"
					dataTest="message-schedule-save-button"
					disabled={!this.state.dirty}
					loading={this.state.saving}
					onClick={this.save}
				>
					<FontAwesomeIcon icon={faSave} />
					&nbsp;
					<FormattedMessage id="actions.save" />
				</Button>
				<Button
					dataTest="message-schedule-cancel-button"
					disabled={this.state.saving}
					onClick={this.cancel}
				>
					<FormattedMessage id="actions.cancel" />
				</Button>
			</Space>
		)
	}

	// stable wrapper identity: an inline arrow in render() would remount the
	// toolbar on every render and close the datepicker popover
	renderToolbar = (toolbarProps) => (
		<CustomCalendarToolbar
			{...toolbarProps}
			timezoneSelector={
				!this.state.editable && (
					<ScheduleTimezoneSelect
						options={this.state.timezoneOptions}
						value={this.state.timezoneKey}
						onChange={this.selectTimezone}
					/>
				)
			}
			actions={this.renderActions()}
		/>
	)

	calendarComponents = { toolbar: this.renderToolbar }

	render() {
		return (
			<div className="noAllDay noResize">
				<DnDCalendar
					localizer={localizer}
					events={this.state.calendarEvents || []}
					formats={formats}
					selectable={this.state.editable}
					style={{ height: this.state.height }}
					views={['week']}
					defaultView="week"
					drilldownView="week"
					step={5}
					timeslots={12}
					date={this.state.date}
					onNavigate={(date) => this.setState({ date })}
					getNow={this.state.getNow}
					showMultiDayTimes={true}
					titleAccessor={this.titleAccessor}
					tooltipAccessor={this.titleAccessor}
					draggableAccessor={() => this.state.editable}
					resizableAccessor={() => false}
					onEventDrop={this.onEventDrop}
					onEventResize={() => {}}
					onSelectSlot={this.handleSelect}
					onSelectEvent={this.handleSelect}
					onRangeChange={this.setRange}
					onView={(e) =>
						this.setState({ height: e === 'month' ? 600 : null })
					}
					eventPropGetter={(calEvent) => {
						const event = this.state.events[calEvent.id]
						if (event) {
							return {
								style: {
									backgroundColor:
										event.media ?
											event.color ?
												event.color
											:	Colors.status.blue.backgroundColor
										:	Colors.status.orange.backgroundColor,
									border: `1px solid ${
										event.media ?
											event.color ?
												'#FFF'
											:	Colors.status.blue.color
										:	Colors.status.orange.borderColor
									}`,
									color:
										event.media ?
											event.color ?
												'#FFF'
											:	Colors.status.blue.color
										:	Colors.status.orange.color,
									fontSize: 8
								}
							}
						}
						return null
					}}
					components={this.calendarComponents}
				/>
				{this.state.editing && (
					<EditScheduleEvent
						event={this.state.editing}
						onClose={(id, rule, media) => {
							if (id) {
								this.setState({
									editing: false,
									creating: null
								})
								this.setEvent(id, rule, media)
							} else {
								// the dialog was cancelled, discard the event
								// created on slot selection if there is one
								this.setState((state) => {
									let events = { ...state.events }
									if (state.creating) {
										delete events[state.creating]
									}
									return {
										editing: false,
										creating: null,
										events
									}
								})
							}
						}}
					/>
				)}
				<Prompt
					when={this.state.dirty}
					message={JSON.stringify({
						title: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.title'
						}),
						message: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.message'
						}),
						leaveText: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.leave'
						}),
						stayText: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.stay'
						})
					})}
				/>
			</div>
		)
	}
}

export default withRouter(withApollo(injectIntl(Scheduler)))
